import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useWindowSize } from 'react-use';

interface MenuSidebarProps {
  width: number;
  onHide?: () => void;
  onShow?: () => void;
  right?: JSX.Element | null;
  children?: React.ReactNode;
  className?: string;
  breakPoint?: number;
}

export const MenuSideBar: FC<MenuSidebarProps> = ({
  right,
  width,
  onHide,
  onShow,
  children,
  className,
  breakPoint = 1024,
}) => {
  const { width: windowWidth } = useWindowSize();
  const [shouldHide, setShouldHide] = useState(false);

  // this is wrapped in a useEffect to prevent the sidebar from flickering when the window is resized
  // and a warning being shown because the server side rendering is potentially out of sync with the client side rendering
  useEffect(() => {
    setShouldHide(windowWidth < breakPoint);
  }, [windowWidth, breakPoint]);

  return (
    <div className={clsx('h-full', className)}>
      <motion.div
        exit={{ x: -(width - 12) }}
        onAnimationStart={(def) => {
          if (!shouldHide) return;

          if ((def as { x: number }).x === 0) {
            onShow?.();
          } else {
            onHide?.();
          }
        }}
        whileHover={{ x: 0 }}
        initial={{ x: -(width - 12) }}
        style={{ width: `${width}px` }}
        transition={{ ease: 'easeInOut' }}
        className={clsx(
          !shouldHide && '!transform',
          'MenuSidebar-motion fixed z-[9] flex h-screen transform-none flex-col border-l border-r border-black bg-darkMode-gray-dark'
        )}
      >
        {children}
      </motion.div>
      <div className="h-[inherit]" style={{ paddingLeft: !shouldHide ? `${width}px` : '12px' }}>
        {right}
      </div>
    </div>
  );
};
